import type { CellValue } from "../api/gameData";

interface CellProps {
  value: CellValue;
  index: number;
  onClick: (index: number) => void;
  isWinning: boolean;
  disabled: boolean;
}

export function Cell({ value, index, onClick, isWinning, disabled }: CellProps) {
  const isX = value === "X";
  const isO = value === "O";

  const borderClasses = [
    index % 3 !== 2 ? "border-r" : "",
    index < 6 ? "border-b" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      onClick={() => onClick(index)}
      disabled={disabled || value !== null}
      aria-label={value ? `Cell ${index + 1}: ${value}` : `Cell ${index + 1}: empty`}
      className={`
        relative aspect-square flex items-center justify-center
        text-5xl sm:text-6xl md:text-7xl font-bold
        transition-all duration-200 ease-out
        border-[var(--game-border)]
        ${borderClasses}
        ${!value && !disabled ? "cursor-pointer hover:bg-[var(--game-surface-hover)] active:scale-95" : "cursor-default"}
        ${isWinning ? "z-10" : ""}
      `}
      style={
        isWinning
          ? ({
              "--glow-color": isX ? "var(--color-x-glow)" : "var(--color-o-glow)",
              animation: "cellGlow 1.5s ease-in-out infinite",
              backgroundColor: isX ? "var(--color-x-deep)" : "var(--color-o-deep)",
            } as React.CSSProperties)
          : undefined
      }
    >
      {value && (
        <span
          className="select-none"
          style={{
            color: isX ? "var(--color-x)" : "var(--color-o)",
            fontFamily: "var(--font-mono)",
            animation: "scaleIn 0.2s ease-out",
            textShadow: isWinning
              ? `0 0 20px ${isX ? "var(--color-x-glow)" : "var(--color-o-glow)"}`
              : "none",
          }}
        >
          {value}
        </span>
      )}
    </button>
  );
}
